import React, { useState, useEffect, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import apiClient from "../../services/api";
import { Program } from "../../types/program";
import HeroSection, { HeroTheme } from "./HeroSection";
import SmartSidebar from "./SmartSidebar";

interface ProgramPageLayoutProps {
  slug: string;
  theme?: HeroTheme;
  children: (program: Program) => ReactNode;
}

const themeButtonColors: Record<HeroTheme, string> = {
  blue: "bg-blue-600 hover:bg-blue-700",
  green: "bg-green-600 hover:bg-green-700",
  purple: "bg-purple-600 hover:bg-purple-700",
  teal: "bg-teal-600 hover:bg-teal-700",
  orange: "bg-orange-600 hover:bg-orange-700",
};

const themeSpinnerColors: Record<HeroTheme, string> = {
  blue: "border-blue-600",
  green: "border-green-600",
  purple: "border-purple-600",
  teal: "border-teal-600",
  orange: "border-orange-600",
};

const ProgramPageLayout: React.FC<ProgramPageLayoutProps> = ({ slug, theme = "blue", children }) => {
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.getProgram(slug);
        if (response.success) {
          setProgram(response.data.program || response.data);
        } else {
          setError("Program not found");
        }
      } catch (err) {
        console.error("Error fetching program:", err);
        setError("Failed to load program details");
      } finally {
        setLoading(false);
      }
    };
    fetchProgram();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${themeSpinnerColors[theme]} mx-auto mb-4`}></div>
          <p className="text-gray-600">Loading program details...</p>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Program Not Found</h1>
            <p className="text-gray-600 mb-8">{error || "The program you are looking for does not exist."}</p>
            <Link
              to="/programs"
              className={`inline-flex items-center px-6 py-3 ${themeButtonColors[theme]} text-white font-semibold rounded-lg transition-colors`}
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Programs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HeroSection program={program} theme={theme} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {children(program)}
          </div>
          <div className="lg:col-span-1">
            <SmartSidebar program={program} slug={slug} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramPageLayout;
